import { useRef, useState } from 'react'
import Image from 'next/image'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'

const getCarouselPageIndex = (swiper, direction) => {
  const { activeIndex, slidesGrid, width } = swiper
  const currentPosition = slidesGrid[activeIndex]

  if (direction === 'previous') {
    const targetPosition = Math.max(currentPosition - width, 0)
    for (let index = activeIndex - 1; index >= 0; index -= 1) {
      if (slidesGrid[index] <= targetPosition) return index
    }
    return 0
  }

  const targetPosition = currentPosition + width
  const nextIndex = slidesGrid.findIndex(
    (position, index) => index > activeIndex && position >= targetPosition
  )
  return nextIndex === -1 ? slidesGrid.length - 1 : nextIndex
}

const ImageCardCarousel = ({ title, items = [], onImageOpen }) => {
  const swiperRef = useRef(null)
  const [isAtStart, setIsAtStart] = useState(true)
  const [isAtEnd, setIsAtEnd] = useState(items.length <= 2)

  if (!items.length) return null

  const updateNavigationState = (swiper) => {
    setIsAtStart(swiper.isBeginning)
    setIsAtEnd(swiper.isEnd)
  }

  return (
    <Box component="section" mb={{ xs: 12, md: 18 }}>
      <Box
        mb={6}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography component="h2" variant="h5">
          {title}
        </Typography>
        <Box display="flex" gap={3}>
          <IconButton
            aria-label={`上一排${title}`}
            disabled={isAtStart}
            size="small"
            onClick={() => {
              const swiper = swiperRef.current
              if (!swiper) return
              swiper.slideTo(getCarouselPageIndex(swiper, 'previous'))
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            aria-label={`下一排${title}`}
            disabled={isAtEnd}
            size="small"
            onClick={() => {
              const swiper = swiperRef.current
              if (!swiper) return
              swiper.slideTo(getCarouselPageIndex(swiper, 'next'))
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      <Box mr={{ xs: -4, lg: -9, xl: -12, xxl: -16 }} overflow="hidden">
        <Swiper
          slidesPerView="auto"
          spaceBetween={16}
          threshold={8}
          grabCursor
          watchOverflow
          breakpoints={{
            768: {
              spaceBetween: 16,
              slidesOffsetAfter: 16,
            },
            992: {
              spaceBetween: 16,
              slidesOffsetAfter: 36,
            },
            1200: {
              spaceBetween: 24,
              slidesOffsetAfter: 48,
            },
            1600: {
              spaceBetween: 32,
              slidesOffsetAfter: 64,
            },
          }}
          onSwiper={(swiper) => {
            swiperRef.current = swiper
            updateNavigationState(swiper)
          }}
          onSlideChange={updateNavigationState}
          onReachBeginning={updateNavigationState}
          onReachEnd={updateNavigationState}
          onFromEdge={updateNavigationState}
          onResize={updateNavigationState}
          style={{ overflow: 'visible' }}
        >
          {items.map((item) => {
            const imageUrl = item?.image?.url
            const itemName = item?.name || ''

            return (
              <SwiperSlide
                key={item._id}
                style={{
                  width: 'auto',
                }}
              >
                <Box
                  width={{
                    xs: '70vw',
                    sm: '36vw',
                    md: '25vw',
                    xxl: '400px',
                  }}
                >
                  <Box p={3} borderRadius="16px" sx={{ aspectRatio: '1 / 1' }}>
                    <Box position="relative" width="100%" height="100%">
                      <ButtonBase
                        aria-label={`查看${itemName || title}圖片`}
                        onClick={() =>
                          onImageOpen?.({ src: imageUrl, alt: itemName })
                        }
                        sx={{
                          position: 'relative',
                          display: 'block',
                          width: '100%',
                          height: '100%',
                          borderRadius: '12px',
                          overflow: 'hidden',
                        }}
                      >
                        <Image
                          src={imageUrl}
                          alt={itemName}
                          fill
                          draggable={false}
                          sizes="(max-width: 575px) 70vw, (max-width: 767px) 36vw, (max-width: 1600px) 25vw, 400px"
                          style={{ objectFit: 'contain' }}
                        />
                      </ButtonBase>
                      <Box
                        position="absolute"
                        right={{ xs: '6px', sm: '10px', lg: '6px' }}
                        bottom={{ xs: '6px', sm: '10px', lg: '6px' }}
                        zIndex={1}
                        width={{ xs: '32px', lg: '40px' }}
                        height={{ xs: '32px', lg: '40px' }}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="50%"
                        bgcolor="background.default"
                        boxShadow="0 3px 3px 0 rgb(0 0 0 / 25%)"
                        sx={{
                          pointerEvents: 'none',
                          '--image-open-icon-size': {
                            xs: '20px',
                            lg: '24px',
                          },
                        }}
                      >
                        <Image
                          src="/img/ic_image_open.svg"
                          alt=""
                          width={24}
                          height={24}
                          draggable={false}
                          style={{
                            width: 'var(--image-open-icon-size)',
                            height: 'var(--image-open-icon-size)',
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Typography
                    mt={3}
                    align="center"
                    variant="titleMedium"
                    color="text.minus2"
                    lineHeight={1}
                    letterSpacing={0}
                    display="-webkit-box"
                    overflow="hidden"
                    sx={{
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                    }}
                  >
                    {itemName}
                  </Typography>
                </Box>
              </SwiperSlide>
            )
          })}
        </Swiper>
      </Box>
    </Box>
  )
}

export default ImageCardCarousel
